import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getEmployeeDetails, updateEmployee, getDepartments, getWorkShifts, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { getPositions } from '../../../../redux/Slices/PositionsSlice';
import { getRoles } from '../../../../redux/Slices/RolesSlice';
import { FaArrowLeft, FaSave, FaPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import axiosInstance from '../../../../shared/utils.js/axiosConfig';
import { BASE_URL, getToken, getCurrentLanguage } from '../../../../shared/utils.js/utils';
import { applyFieldFilter } from '../../../../shared/utils.js/fieldValidation';
import { saveDropdownCache, getDropdownCache, generateDropdownCacheKey, getAllDropdownCacheForQuery, isOnline } from '../../../../shared/utils.js/offlineQueue';


// edit employee component
const EditEmployee = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const { id } = useParams();
  const { employeeDetails, departments, positions, workShifts, isLoading, error, success } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    username: '',
    password: '',
    password_confirmation: '',
    email: '',
    phone: '',
    employee_code: '',
    national_id: '',
    birth_date: '',
    gender: 'male',
    department_id: '',
    position_id: '',
    work_shift_id: '',
    role_id: '',
    is_active: true,
    can_start_tasks_without_approval: false,
  });
  const [images, setImages] = useState([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [usernameStatus, setUsernameStatus] = useState('idle'); // idle | checking | available | exists | error
  const [usernameMessage, setUsernameMessage] = useState('');
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const usernameCheckTimeoutRef = useRef(null);
  const originalUsernameRef = useRef(''); // Store original username to skip check if unchanged

  // Role dropdown state
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [roleOptions, setRoleOptions] = useState([]);
  const [rolePage, setRolePage] = useState(1);
  const [rolePagination, setRolePagination] = useState(null);
  const roleSearchInputRef = useRef(null);

  const normalizeListResponse = (payload) => {
    if (!payload) return { items: [], pagination: null };

    let items = [];
    let pagination = null;

    if (Array.isArray(payload?.data?.data)) {
      items = payload.data.data;
      pagination = payload.data?.meta || payload.data?.pagination || null;
    } else if (Array.isArray(payload?.data)) {
      items = payload.data;
      pagination = payload.meta || payload.pagination || null;
    } else if (Array.isArray(payload?.items)) {
      items = payload.items;
      pagination = payload.pagination || payload.meta || null;
    } else if (Array.isArray(payload)) {
      items = payload;
      pagination = null;
    } else {
      items = [];
      pagination = payload.pagination || payload.meta || null;
    }

    // Normalize pagination to ensure it has current_page and last_page
    if (pagination) {
      pagination = {
        current_page: pagination.current_page || pagination.page || 1,
        last_page: pagination.last_page || pagination.total_pages || 1,
        per_page: pagination.per_page || 10,
        total: pagination.total || 0,
      };
    }

    return { items, pagination };
  };

  useEffect(() => {
    if (id) {
      dispatch(getEmployeeDetails(id));
    }

    const loadDropdowns = async () => {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) return;

      // Load departments from cache first
      let cachedDepts = null;
      try {
        const deptCacheKey = generateDropdownCacheKey('departments', { page: 1, per_page: 1000 });
        cachedDepts = await getDropdownCache(deptCacheKey);
        if (cachedDepts && Array.isArray(cachedDepts.items) && cachedDepts.items.length > 0) {
          // Update Redux state from cache
          dispatch(getDepartments({ per_page: 1000 }));
        }
      } catch (e) {
        console.warn('Error loading departments from cache:', e);
      }

      // Load positions from cache first
      let cachedPos = null;
      try {
        const posCacheKey = generateDropdownCacheKey('positions', { page: 1, per_page: 1000 });
        cachedPos = await getDropdownCache(posCacheKey);
        if (cachedPos && Array.isArray(cachedPos.items) && cachedPos.items.length > 0) {
          // Update Redux state from cache
          dispatch(getPositions({ per_page: 1000 }));
        }
      } catch (e) {
        console.warn('Error loading positions from cache:', e);
      }

      // Load work shifts from cache first
      let cachedShifts = null;
      try {
        const shiftCacheKey = generateDropdownCacheKey('work-shifts', { page: 1, per_page: 1000 });
        cachedShifts = await getDropdownCache(shiftCacheKey);
        if (cachedShifts && Array.isArray(cachedShifts.items) && cachedShifts.items.length > 0) {
          // Update Redux state from cache
          dispatch(getWorkShifts({ per_page: 1000 }));
        }
      } catch (e) {
        console.warn('Error loading work shifts from cache:', e);
      }

      // Load roles from cache first (preload even if dropdown not open)
      let cachedRoles = null;
      try {
        const roleCacheKey = generateDropdownCacheKey('roles', { page: 1, per_page: 1000 });
        cachedRoles = await getDropdownCache(roleCacheKey);
        if (cachedRoles && Array.isArray(cachedRoles.items) && cachedRoles.items.length > 0) {
          setRoleOptions(cachedRoles.items);
          setRolePagination(cachedRoles.pagination);
        }
      } catch (e) {
        console.warn('Error loading roles from cache:', e);
      }

      // Fetch from API if online (will update cache automatically via axios interceptor)
      if (isOnline()) {
        // Preload all dropdowns automatically to ensure offline support
        try {
          // Preload departments
          if (!cachedDepts || !Array.isArray(cachedDepts.items) || cachedDepts.items.length === 0) {
            const deptResponse = await axiosInstance.get(`${BASE_URL}/departments`, {
              params: { page: 1, per_page: 1000 },
              headers: {
                Authorization: `Bearer ${token}`,
                'Accept-Language': currentLang,
              },
            });
            const deptData = deptResponse.data?.data || deptResponse.data || [];
            if (Array.isArray(deptData) && deptData.length > 0) {
              const deptCacheKey = generateDropdownCacheKey('departments', { page: 1, per_page: 1000 });
              await saveDropdownCache(deptCacheKey, deptData, { current_page: 1, last_page: 1, per_page: 1000, total: deptData.length });
              dispatch(getDepartments({ per_page: 1000 }));
            }
          } else {
            dispatch(getDepartments({ per_page: 1000 }));
          }
        } catch (deptError) {
          console.warn('Error preloading departments:', deptError);
          // Fallback: use cache if available
          if (cachedDepts && Array.isArray(cachedDepts.items) && cachedDepts.items.length > 0) {
            dispatch(getDepartments({ per_page: 1000 }));
          }
        }

        try {
          // Preload positions
          if (!cachedPos || !Array.isArray(cachedPos.items) || cachedPos.items.length === 0) {
            const posResponse = await axiosInstance.get(`${BASE_URL}/positions`, {
              params: { page: 1, per_page: 1000 },
              headers: {
                Authorization: `Bearer ${token}`,
                'Accept-Language': currentLang,
              },
            });
            const posData = posResponse.data?.data || posResponse.data || [];
            if (Array.isArray(posData) && posData.length > 0) {
              const posCacheKey = generateDropdownCacheKey('positions', { page: 1, per_page: 1000 });
              await saveDropdownCache(posCacheKey, posData, { current_page: 1, last_page: 1, per_page: 1000, total: posData.length });
              dispatch(getPositions({ per_page: 1000 }));
            }
          } else {
            dispatch(getPositions({ per_page: 1000 }));
          }
        } catch (posError) {
          console.warn('Error preloading positions:', posError);
          // Fallback: use cache if available
          if (cachedPos && Array.isArray(cachedPos.items) && cachedPos.items.length > 0) {
            dispatch(getPositions({ per_page: 1000 }));
          }
        }

        try {
          // Preload work shifts
          if (!cachedShifts || !Array.isArray(cachedShifts.items) || cachedShifts.items.length === 0) {
            const shiftResponse = await axiosInstance.get(`${BASE_URL}/work-shifts`, {
              params: { page: 1, per_page: 1000 },
              headers: {
                Authorization: `Bearer ${token}`,
                'Accept-Language': currentLang,
              },
            });
            const shiftData = shiftResponse.data?.data || shiftResponse.data || [];
            if (Array.isArray(shiftData) && shiftData.length > 0) {
              const shiftCacheKey = generateDropdownCacheKey('work-shifts', { page: 1, per_page: 1000 });
              await saveDropdownCache(shiftCacheKey, shiftData, { current_page: 1, last_page: 1, per_page: 1000, total: shiftData.length });
              dispatch(getWorkShifts({ per_page: 1000 }));
            }
          } else {
            dispatch(getWorkShifts({ per_page: 1000 }));
          }
        } catch (shiftError) {
          console.warn('Error preloading work shifts:', shiftError);
          // Fallback: use cache if available
          if (cachedShifts && Array.isArray(cachedShifts.items) && cachedShifts.items.length > 0) {
            dispatch(getWorkShifts({ per_page: 1000 }));
          }
        }

        // Preload roles automatically (even if dropdown not opened) to ensure offline support
        try {
          const roleParams = { page: 1, per_page: 1000 };
          const roleCacheKey = generateDropdownCacheKey('roles', roleParams);

          // Check if already cached
          const existingCache = await getDropdownCache(roleCacheKey);
          if (!existingCache || !Array.isArray(existingCache.items) || existingCache.items.length === 0) {
            // Fetch roles to populate cache
            const roleResponse = await axiosInstance.get(`${BASE_URL}/roles`, {
              params: roleParams,
              headers: {
                Authorization: `Bearer ${token}`,
                'Accept-Language': currentLang,
              },
            });
            const roleData = roleResponse.data?.data || roleResponse.data || [];
            if (Array.isArray(roleData) && roleData.length > 0) {
              const { items, pagination: paginationMeta } = normalizeListResponse(roleResponse.data);

              // Save to cache
              try {
                await saveDropdownCache(roleCacheKey, items, paginationMeta);
                // Also set in state for immediate use
                setRoleOptions(items);
                setRolePagination(paginationMeta);
              } catch (saveError) {
                console.warn('Error saving roles to cache:', saveError);
              }
            }
          } else {
            // Use existing cache
            setRoleOptions(existingCache.items);
            setRolePagination(existingCache.pagination);
          }
        } catch (roleError) {
          console.warn('Error preloading roles:', roleError);
          // Fallback: use cache if available
          if (cachedRoles && Array.isArray(cachedRoles.items) && cachedRoles.items.length > 0) {
            setRoleOptions(cachedRoles.items);
            setRolePagination(cachedRoles.pagination);
          }
        }
      } else {
        // If offline, try to get from cache and update Redux if needed
        try {
          const allDepts = await getAllDropdownCacheForQuery('departments', { per_page: 1000 });
          if (allDepts.length > 0) {
            // Try to trigger Redux update through dispatch (will use cache via interceptor)
            dispatch(getDepartments({ per_page: 1000 }));
          }
        } catch (e) {
          console.warn('Error loading departments offline:', e);
        }

        try {
          const allPos = await getAllDropdownCacheForQuery('positions', { per_page: 1000 });
          if (allPos.length > 0) {
            // Try to trigger Redux update through dispatch (will use cache via interceptor)
            dispatch(getPositions({ per_page: 1000 }));
          }
        } catch (e) {
          console.warn('Error loading positions offline:', e);
        }

        try {
          const allShifts = await getAllDropdownCacheForQuery('work-shifts', { per_page: 1000 });
          if (allShifts.length > 0) {
            // Try to trigger Redux update through dispatch (will use cache via interceptor)
            dispatch(getWorkShifts({ per_page: 1000 }));
          }
        } catch (e) {
          console.warn('Error loading work shifts offline:', e);
        }

        // Load roles from cache if offline
        if (cachedRoles && Array.isArray(cachedRoles.items) && cachedRoles.items.length > 0) {
          setRoleOptions(cachedRoles.items);
          setRolePagination(cachedRoles.pagination);
        } else {
          // Try to get any cached roles
          try {
            const allCachedRoles = await getAllDropdownCacheForQuery('roles', { per_page: 1000 });
            if (allCachedRoles.length > 0) {
              setRoleOptions(allCachedRoles);
              setRolePagination({ current_page: 1, last_page: 1, per_page: 1000, total: allCachedRoles.length });
            }
          } catch (e) {
            console.warn('Error loading roles offline:', e);
          }
        }
      }
    };

    loadDropdowns();
  }, [id, dispatch]);

  const fetchRoles = useCallback(
    async (page = rolePage, search = roleSearchTerm) => {
      try {
        const queryParams = { page, per_page: 1000, search: search || undefined };
        const cacheKey = generateDropdownCacheKey('roles', queryParams);

        // Try cache first
        try {
          const cachedData = await getDropdownCache(cacheKey);
          if (cachedData && Array.isArray(cachedData.items)) {
            setRoleOptions(cachedData.items);
            setRolePagination(cachedData.pagination);
          }
        } catch (cacheError) {
          console.warn('Error loading roles from cache:', cacheError);
        }

        // Fetch from API if online
        if (isOnline()) {
          try {
            const payload = await dispatch(
              getRoles(queryParams)
            ).unwrap();
            const { items, pagination: paginationMeta } = normalizeListResponse(payload);

            // Save to cache
            try {
              await saveDropdownCache(cacheKey, items, paginationMeta);
            } catch (saveError) {
              console.warn('Error saving roles to cache:', saveError);
            }

            setRoleOptions(items);
            setRolePagination(paginationMeta);
          } catch (error) {
            // If API fails and we have cached data, use it
            const cachedData = await getDropdownCache(cacheKey);
            if (cachedData && Array.isArray(cachedData.items) && cachedData.items.length > 0) {
              setRoleOptions(cachedData.items);
              setRolePagination(cachedData.pagination);
            } else {
              toast.error(error || t('employees.form.messages.fetchRolesError', { defaultValue: 'فشل جلب الأدوار' }), { rtl: isRTL });
            }
          }
        } else {
          // Offline: try to get all cached roles
          try {
            const allCached = await getAllDropdownCacheForQuery('roles', { per_page: 1000, search: search || '' });
            if (allCached.length > 0) {
              setRoleOptions(allCached);
              setRolePagination({ current_page: 1, last_page: 1, per_page: 1000, total: allCached.length });
            }
          } catch (offlineError) {
            console.warn('Error loading roles offline:', offlineError);
          }
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        toast.error(error || t('employees.form.messages.fetchRolesError', { defaultValue: 'فشل جلب الأدوار' }), { rtl: isRTL });
      }
    },
    [dispatch, t, isRTL]
  );

  useEffect(() => {
    if (roleDropdownOpen) {
      fetchRoles(rolePage, roleSearchTerm);
    }
  }, [roleDropdownOpen, rolePage, fetchRoles]);

  useEffect(() => {
    if (roleSearchTerm !== undefined && roleDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setRolePage(1);
        fetchRoles(1, roleSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [roleSearchTerm, roleDropdownOpen, fetchRoles]);



  useEffect(() => {
    if (employeeDetails) {
      const departmentId = employeeDetails.department_id ||
        (departments?.find(dept => dept.name === employeeDetails.department)?.id) || '';

      const positionId = employeeDetails.position_id ||
        (positions?.find(pos => pos.name === employeeDetails.position)?.id) || '';

      const workShiftId = employeeDetails.work_shift_id ||
        employeeDetails.work_shift?.id || '';

      const roleId = employeeDetails.role_id ||
        employeeDetails.role?.id || '';

      const canStartWithoutApproval =
        employeeDetails.can_start_tasks_without_approval === true ||
        employeeDetails.can_start_tasks_without_approval === 1 ||
        employeeDetails.can_start_tasks_without_approval === "1";


      const username = employeeDetails.username || '';
      setFormData({
        name_en: employeeDetails.name_en || employeeDetails.name || '',
        name_ar: employeeDetails.name_ar || employeeDetails.name || '',
        username: username,
        email: employeeDetails.email || '',
        phone: employeeDetails.phone || '',
        employee_code: employeeDetails.employee_code || '',
        national_id: employeeDetails.national_id || '',
        birth_date: employeeDetails.birth_date ? employeeDetails.birth_date.split('T')[0] : '',
        gender: employeeDetails.gender || 'male',
        department_id: departmentId,
        position_id: positionId,
        work_shift_id: workShiftId,
        role_id: roleId,
        is_active: employeeDetails.is_active !== undefined ? employeeDetails.is_active : true,
        can_start_tasks_without_approval: canStartWithoutApproval,
      });
      originalUsernameRef.current = username;
      setImages(employeeDetails.images || []);
      setImagesToDelete([]);
      // Reset username status when loading employee details
      setUsernameStatus('idle');
      setUsernameMessage('');
      setUsernameSuggestions([]);
    }
  }, [employeeDetails, departments, positions, workShifts]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      // Reset images to delete list on successful update
      setImagesToDelete([]);
      dispatch(clearSuccess());
      navigate('/employees');
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, navigate, dispatch, isRTL]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'can_start_tasks_without_approval') {
      setFormData(prev => ({ ...prev, [name]: value === '1' }));
      return;
    }
    const finalValue = type === 'checkbox' ? checked : applyFieldFilter(name, value);
    setFormData(prev => ({ ...prev, [name]: finalValue }));

    if (name === 'username') {
      const username = (finalValue || '').trim();
      if (usernameCheckTimeoutRef.current) {
        clearTimeout(usernameCheckTimeoutRef.current);
      }
      // If username is same as original, don't check
      if (!username || username === originalUsernameRef.current) {
        setUsernameStatus('idle');
        setUsernameMessage('');
        setUsernameSuggestions([]);
        return;
      }
      setUsernameStatus('checking');
      setUsernameMessage(t('employees.form.usernameMessages.checking'));
      setUsernameSuggestions([]);
      usernameCheckTimeoutRef.current = setTimeout(() => {
        checkUsernameAvailability(username, true);
      }, 500);
    }
  };

  const isUsernameAvailable = async (username) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        return false;
      }
      const response = await axios.post(
        `${BASE_URL}/auth/check-username`,
        { username },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': currentLang,
          },
        }
      );
      const exists = response?.data?.data?.exists;
      return !exists;
    } catch {
      return false;
    }
  };

  const generateUsernameSuggestions = async (baseUsername) => {
    const suggestions = [];
    let attempt = 1;
    const maxAttempts = 30;

    while (suggestions.length < 3 && attempt <= maxAttempts) {
      const candidate = `${baseUsername}${attempt}`;
      // Avoid suggesting the same username
      if (candidate !== baseUsername && !(await isUsernameAvailable(candidate))) {
        attempt += 1;
        continue;
      }
      if (!suggestions.includes(candidate)) {
        suggestions.push(candidate);
      }
      attempt += 1;
    }

    setUsernameSuggestions(suggestions);
  };

  const checkUsernameAvailability = async (username, withSuggestions = false) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        setUsernameStatus('error');
        setUsernameMessage(t('employees.form.validation.requiredFields'));
        return;
      }

      const response = await axios.post(
        `${BASE_URL}/auth/check-username`,
        { username },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': currentLang,
          },
        }
      );
      const exists = response?.data?.data?.exists;
      if (exists) {
        setUsernameStatus('exists');
        setUsernameMessage(t('employees.form.usernameMessages.exists'));
        if (withSuggestions) {
          await generateUsernameSuggestions(username);
        }
      } else {
        setUsernameStatus('available');
        setUsernameMessage(t('employees.form.usernameMessages.available', { username }));
        setUsernameSuggestions([]);
      }
    } catch {
      setUsernameStatus('error');
      setUsernameMessage(t('employees.form.usernameMessages.exists'));
    }
  };

  const handleUsernameSuggestionClick = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      username: suggestion,
    }));
    setUsernameSuggestions([]);
    checkUsernameAvailability(suggestion, false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name_en || !formData.name_ar || !formData.birth_date || !formData.position_id) {
      toast.error(t('employees.form.validation.requiredFields'), { rtl: isRTL });
      return;
    }

    // Validate birth date is not in the future
    if (formData.birth_date && formData.birth_date > new Date().toISOString().split('T')[0]) {
      toast.error(
        t('employees.form.validation.futureBirthDate', { defaultValue: 'تاريخ الميلاد لا يمكن أن يكون في المستقبل' }),
        { rtl: isRTL }
      );
      return;
    }

    const trimmedUsername = (formData.username || '').trim();
    const hasUsername = trimmedUsername.length > 0;
    const usernameChanged = hasUsername && trimmedUsername !== originalUsernameRef.current;

    // If username is provided (new or changed), validate it and require password
    if (hasUsername) {
      // If username is new or changed, require password
      if (usernameChanged) {
        // Validate password fields if username is new or changed
        if (!formData.password || !formData.password_confirmation) {
          toast.error(t('employees.form.validation.passwordRequired', { defaultValue: 'Password and password confirmation are required when entering or changing the username' }), { rtl: isRTL });
          return;
        }

        if (formData.password !== formData.password_confirmation) {
          toast.error(t('employees.form.validation.passwordMismatch', { defaultValue: 'Password and password confirmation do not match' }), { rtl: isRTL });
          return;
        }
      }

      // Only validate username if it has changed
      if (usernameChanged) {
        // Prevent submit if we already know username exists
        if (usernameStatus === 'exists') {
          toast.error(t('employees.form.usernameMessages.exists'), { rtl: isRTL });
          return;
        }

        // Final check on submit to avoid race conditions
        const available = await isUsernameAvailable(trimmedUsername);
        if (!available) {
          setUsernameStatus('exists');
          setUsernameMessage(t('employees.form.usernameMessages.exists'));
          await generateUsernameSuggestions(trimmedUsername);
          toast.error(t('employees.form.usernameMessages.exists'), { rtl: isRTL });
          return;
        }
      }
    }

    // Create FormData for file upload
    const formDataToSend = new FormData();

    // Add all form fields
    Object.keys(formData).forEach(key => {
      // Skip employee code: not sent on edit
      if (key === 'employee_code') {
        return;
      }
      // Skip password fields if username is not provided or not changed
      if (!hasUsername && (key === 'password' || key === 'password_confirmation')) {
        return;
      }
      // Skip password fields if username hasn't changed (no need to update password)
      if (hasUsername && !usernameChanged && (key === 'password' || key === 'password_confirmation')) {
        return;
      }
      // Skip username if not provided
      if (!hasUsername && key === 'username') {
        return;
      }

      if (formData[key] !== null && formData[key] !== undefined) {
        if (key === 'is_active' || key === 'can_start_tasks_without_approval') {
          formDataToSend.append(key, formData[key] ? '1' : '0');
        } else if (key === 'department_id' || key === 'position_id' || key === 'work_shift_id' || key === 'role_id') {
          formDataToSend.append(key, formData[key] || '');
        } else if (key === 'email') {
          const emailValue = formData[key].trim();
          formDataToSend.append(key, emailValue === '' ? 'null' : emailValue);
        } else {
          formDataToSend.append(key, formData[key]);
        }
      }
    });

    // Handle email separately if it's empty (send as null)
    if (formData.email === '' || (formData.email && formData.email.trim() === '')) {
      formDataToSend.set('email', 'null');
    }

    // Ensure numeric flag reaches backend as 1/0
    formDataToSend.set(
      'can_start_tasks_without_approval',
      formData.can_start_tasks_without_approval ? '1' : '0'
    );

    // Only set username if it was provided
    if (!hasUsername) {
      formDataToSend.delete('username');
    }

    // Ensure department_id is always sent (even if empty)
    if (!formDataToSend.has('department_id')) {
      formDataToSend.append('department_id', formData.department_id || '');
    }

    // Ensure position_id is always sent (even if empty)
    if (!formDataToSend.has('position_id')) {
      formDataToSend.append('position_id', formData.position_id || '');
    }

    // Ensure work_shift_id is always sent (even if empty)
    if (!formDataToSend.has('work_shift_id')) {
      formDataToSend.append('work_shift_id', formData.work_shift_id || '');
    }

    // Ensure role_id is always sent (even if empty)
    if (!formDataToSend.has('role_id')) {
      formDataToSend.append('role_id', formData.role_id || '');
    }

    // Add images as files - only add if there are actual files
    if (images.length > 0) {
      images.forEach((image, index) => {
        if (image.file) {
          formDataToSend.append(`images[${index}]`, image.file);
        }
      });
    }

    // Add images to delete
    if (imagesToDelete.length > 0) {
      imagesToDelete.forEach((imageId, index) => {
        formDataToSend.append(`images_to_delete[${index}]`, imageId);
      });
    }

    // Add _method for PUT request
    formDataToSend.append('_method', 'PUT');

    await dispatch(updateEmployee({ id, employeeData: formDataToSend }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploadingImages(true);

    // Simulate loading for better UX
    setTimeout(() => {
      const newImages = files.map(file => ({
        id: Date.now() + Math.random(),
        url: URL.createObjectURL(file),
        file: file
      }));
      setImages(prev => [...prev, ...newImages]);
      setIsUploadingImages(false);
    }, 1000);
  };

  const removeImage = (imageId) => {
    // إذا كانت الصورة موجودة في الخادم (لها id)، أضفها لقائمة الحذف
    if (imageId && typeof imageId === 'number') {
      setImagesToDelete(prev => [...prev, imageId]);
    }
    // احذف الصورة من القائمة المحلية
    setImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleCancel = () => {
    navigate('/employees');
  };

  // Remove loading screen to prevent blocking after form submission
  // if (isLoading) {
  //   return (
  //     <div style={{ 
  //       display: 'flex', 
  //       justifyContent: 'center', 
  //       alignItems: 'center', 
  //       height: '50vh',
  //       color: 'white'
  //     }}>
  //       <div>جاري التحميل...</div>
  //     </div>
  //   );
  // }

  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      {/* العنوان */}
      <div style={{ marginBottom: '30px' }}>
        <button
          onClick={() => navigate('/employees')}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '10px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaArrowLeft />
          {t('employees.form.back')}
        </button>

        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'white',
          margin: 0,
          textAlign
        }}>
          {t('employees.form.editTitle')}
        </h1>
      </div>

      {/* النموذج */}
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '30px',
        border: '1px solid #333'
      }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
            {/* العمود الأيسر */}
            <div style={{ flex: '1', minWidth: '300px' }}>
              {/* الاسم بالعربي */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.nameAr')}*
                </label>
                <input
                  type="text"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleInputChange}
                  placeholder={t('employees.form.placeholders.nameAr')}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    textAlign,
                    direction: dir
                  }}
                />
              </div>

              {/* الاسم بالإنجليزي */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.nameEn')}*
                </label>
                <input
                  type="text"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleInputChange}
                  placeholder={t('employees.form.placeholders.nameEn')}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    textAlign,
                    direction: dir
                  }}
                />
              </div>

              {/* اسم المستخدم */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.username')} <span style={{ color: 'white', fontSize: '12px', fontWeight: 'normal' }}>{t('employees.form.optional')}</span>
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder={t('employees.form.placeholders.username')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    textAlign,
                    direction: dir
                  }}
                />
                {usernameMessage && (
                  <div
                    style={{
                      marginTop: '6px',
                      fontSize: '12px',
                      color:
                        usernameStatus === 'available'
                          ? '#10b981'
                          : usernameStatus === 'checking'
                            ? '#eab308'
                            : '#f97373',
                    }}
                  >
                    {usernameMessage}
                  </div>
                )}
                {usernameSuggestions.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <div
                      style={{
                        marginBottom: '4px',
                        fontSize: '12px',
                        color: '#9ca3af',
                      }}
                    >
                      {t('employees.form.usernameMessages.suggestionsTitle')}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {usernameSuggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => handleUsernameSuggestionClick(suggestion)}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#374151',
                            borderRadius: '999px',
                            border: 'none',
                            color: 'white',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* كلمة المرور - تظهر فقط إذا تم إدخال username أو تغييره */}
              {formData.username.trim() && formData.username.trim() !== originalUsernameRef.current && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: 'white',
                        fontWeight: '500',
                        textAlign
                      }}
                    >
                      {t('employees.form.fields.password', { defaultValue: 'Password' })}
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder={t('employees.form.placeholders.password', { defaultValue: 'Enter password' })}
                      required={formData.username.trim().length > 0 && formData.username.trim() !== originalUsernameRef.current}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        textAlign,
                        direction: dir
                      }}
                    />
                  </div>

                  {/* تأكيد كلمة المرور */}
                  <div style={{ marginBottom: '20px' }}>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: 'white',
                        fontWeight: '500',
                        textAlign
                      }}
                    >
                      {t('employees.form.fields.passwordConfirmation', { defaultValue: 'Confirm Password' })}
                    </label>
                    <input
                      type="password"
                      name="password_confirmation"
                      value={formData.password_confirmation}
                      onChange={handleInputChange}
                      placeholder={t('employees.form.placeholders.passwordConfirmation', { defaultValue: 'Re-enter password' })}
                      required={formData.username.trim().length > 0 && formData.username.trim() !== originalUsernameRef.current}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        textAlign,
                        direction: dir
                      }}
                    />
                  </div>
                </>
              )}

              {/* الرقم القومي */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.nationalId')}
                </label>
                <input
                  type="text"
                  name="national_id"
                  value={formData.national_id}
                  onChange={handleInputChange}
                  placeholder={t('employees.form.placeholders.nationalId')}
                  maxLength={14}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    textAlign,
                    direction: dir
                  }}
                />
              </div>

              {/* البريد الإلكتروني */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.email')} <span style={{ color: 'white', fontSize: '12px', fontWeight: 'normal' }}>{t('employees.form.optional')}</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('employees.form.placeholders.email')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    textAlign,
                    direction: dir
                  }}
                />
              </div>

              {/* إمكانية بدء المهام بدون موافقة */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.canStartTasks', { defaultValue: 'Can start tasks without approval' })}
                </label>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'white', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="can_start_tasks_without_approval"
                      value="1"
                      checked={formData.can_start_tasks_without_approval === true}
                      onChange={handleInputChange}
                      style={{ cursor: 'pointer' }}
                    />
                    {t('employees.common.yes', { defaultValue: 'Yes' })}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'white', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="can_start_tasks_without_approval"
                      value="0"
                      checked={formData.can_start_tasks_without_approval === false}
                      onChange={handleInputChange}
                      style={{ cursor: 'pointer' }}
                    />
                    {t('employees.common.no', { defaultValue: 'No' })}
                  </label>
                </div>
              </div>

            </div>

            {/* العمود الأيمن */}
            <div style={{ flex: '1', minWidth: '300px' }}>
              {/* تاريخ الميلاد */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.birthDate')}*
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="date"
                    name="birth_date"
                    value={formData.birth_date}
                    onChange={handleInputChange}
                    required
                    max={new Date().toISOString().split('T')[0]}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      colorScheme: 'dark',
                      textAlign,
                      direction: dir
                    }}
                  />
                </div>
              </div>

              {/* الجنس */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.gender')}
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    textAlign,
                    direction: dir
                  }}
                >
                  <option value="">{t('employees.form.genderPlaceholder')}</option>
                  <option value="male" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.common.gender.male')}</option>
                  <option value="female" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.common.gender.female')}</option>
                </select>
              </div>

              {/* المسمى الوظيفي */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.position')}*
                </label>
                <select
                  name="position_id"
                  value={formData.position_id}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    textAlign,
                    direction: dir
                  }}
                >
                  <option value="" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.form.positionPlaceholder')}</option>
                  {positions && positions.length > 0 ? (
                    positions.map(position => (
                      <option key={position.id} value={position.id} style={{ backgroundColor: '#1a1f2e' }}>
                        {position.name}
                      </option>
                    ))
                  ) : (
                    <option value="" style={{ backgroundColor: '#1a1f2e' }} disabled>{t('employees.common.loading')}</option>
                  )}
                </select>
              </div>

              {/* مكان العمل */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.workPlace')}
                </label>
                <select
                  name="work_shift_id"
                  value={formData.work_shift_id}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    textAlign,
                    direction: dir
                  }}
                >
                  <option value="" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.form.placeholders.workPlace')}</option>
                  {workShifts && workShifts.length > 0 ? (
                    workShifts.map(shift => (
                      <option key={shift.id} value={shift.id} style={{ backgroundColor: '#1a1f2e' }}>
                        {shift.name}
                      </option>
                    ))
                  ) : (
                    <option value="" style={{ backgroundColor: '#1a1f2e' }} disabled>{t('employees.common.loading')}</option>
                  )}
                </select>
              </div>

              {/* الدور (Role) */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.role', { defaultValue: 'الدور' })} <span style={{ color: 'white', fontSize: '12px', fontWeight: 'normal' }}>{t('employees.form.optional')}</span>
                </label>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div
                    data-dropdown-toggle
                    onClick={() => setRoleDropdownOpen((prev) => !prev)}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: dir,
                      textAlign,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <span>
                      {formData.role_id
                        ? (() => {
                          const selected = roleOptions.find((r) => String(r.id) === String(formData.role_id));
                          return selected
                            ? selected.name
                            : t('employees.form.placeholders.selectRole', { defaultValue: 'اختر الدور' });
                        })()
                        : t('employees.form.placeholders.selectRole', { defaultValue: 'اختر الدور' })}
                    </span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  <DropdownWithSearch
                    isOpen={roleDropdownOpen}
                    searchTerm={roleSearchTerm}
                    onSearchChange={setRoleSearchTerm}
                    searchInputRef={roleSearchInputRef}
                    items={roleOptions}
                    pagination={rolePagination}
                    onPageChange={setRolePage}
                    onClose={() => setRoleDropdownOpen(false)}
                    onSelect={(item) => {
                      setFormData(prev => ({ ...prev, role_id: String(item.id) }));
                      setRoleDropdownOpen(false);
                      setRoleSearchTerm('');
                      setRolePage(1);
                    }}
                    label={t('employees.form.fields.role', { defaultValue: 'الدور' })}
                    isRTL={isRTL}
                    i18n={i18n}
                    itemDisplayKey="name"
                  />
                </div>
              </div>

              {/* حالة الموظف */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.status')}
                </label>
                <select
                  name="is_active"
                  value={formData.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    is_active: e.target.value === 'active'
                  }))}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    textAlign,
                    direction: dir
                  }}
                >
                  <option value="active" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.common.status.active')}</option>
                  <option value="inactive" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.common.status.inactive')}</option>
                </select>
              </div>

              {/* رقم الهاتف */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('employees.form.fields.phone')} <span style={{ color: 'white', fontSize: '12px', fontWeight: 'normal' }}>{t('employees.form.optional')}</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute',
                    [isRTL ? 'right' : 'left']: '15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#AC2000',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    zIndex: 1
                  }}>
                    +2
                  </span>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t('employees.form.placeholders.phone')}
                    style={{
                      width: '100%',
                      padding: isRTL ? '12px 50px 12px 15px' : '12px 15px 12px 50px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      textAlign,
                      direction: dir
                    }}
                  />
                </div>
              </div>

            </div>
          </div>

          {/* القسم - Full Width */}
          <div style={{ marginTop: '30px', width: '100%' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: 'white',
              fontWeight: '500',
              textAlign
            }}>
              {t('employees.form.fields.department')}
            </label>
            <select
              name="department_id"
              value={formData.department_id}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '12px 15px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                textAlign,
                direction: dir
              }}
            >
              <option value="" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.form.departmentPlaceholder')}</option>
              {departments && departments.length > 0 ? (
                departments.map(dept => (
                  <option key={dept.id} value={dept.id} style={{ backgroundColor: '#1a1f2e' }}>
                    {dept.name}
                  </option>
                ))
              ) : (
                <option value="" style={{ backgroundColor: '#1a1f2e' }} disabled>{t('employees.common.loading')}</option>
              )}
            </select>
          </div>

          {/* رفع الصور - Full Width */}
          <div style={{ marginTop: '30px', width: '100%' }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              marginBottom: '20px'
            }}>
              <label style={{
                display: 'block',
                fontWeight: 'bold',
                color: 'white',
                fontSize: '16px',
                textAlign
              }}>
                {t('employees.form.fields.images')}
              </label>

              {/* زر رفع الصور */}
              <div style={{ display: 'flex', justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
                <input
                  type="file"
                  id="imageUpload"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={isUploadingImages}
                />
                <label
                  htmlFor="imageUpload"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: 'transparent',
                    color: 'white',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    cursor: isUploadingImages ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    border: '2px solid #AC2000',
                    opacity: isUploadingImages ? 0.6 : 1,
                    transition: 'all 0.3s ease',
                    flexDirection: isRTL ? 'row-reverse' : 'row'
                  }}
                >
                  {isUploadingImages ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #AC2000',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      {t('employees.form.upload.uploading')}
                    </>
                  ) : (
                    <>
                      <FaPlus />
                      {t('employees.form.upload.add')}
                    </>
                  )}
                </label>
                <style>
                  {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
                </style>
              </div>
            </div>

            {/* عرض الصور المرفوعة */}
            {images.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '15px',
                marginTop: '20px'
              }}>
                {images.map((image, index) => (
                  <div key={image.id || index} style={{ position: 'relative' }}>
                    <img
                      src={image.url || image}
                      alt={`صورة ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '150px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid #333'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(image.id || index)}
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '25px',
                        height: '25px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px'
                      }}
                    >
                      <FaTrash />
                    </button>
                    {/* مؤشر الصور المحذوفة */}
                    {imagesToDelete.includes(image.id) && (
                      <div style={{
                        position: 'absolute',
                        top: '5px',
                        [isRTL ? 'left' : 'right']: '5px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}>
                        {t('employees.form.deletedImages.badge')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* مؤشر الصور المحذوفة */}
            {imagesToDelete.length > 0 && (
              <div style={{
                marginTop: '15px',
                padding: '10px',
                backgroundColor: '#dc3545',
                color: 'white',
                borderRadius: '6px',
                fontSize: '14px',
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}>
                <span>{t('employees.form.deletedImages.notice', { count: imagesToDelete.length })}</span>
                <button
                  type="button"
                  onClick={() => setImagesToDelete([])}
                  style={{
                    backgroundColor: 'transparent',
                    color: 'white',
                    border: '1px solid white',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  {t('employees.form.deletedImages.undo')}
                </button>
              </div>
            )}

            {/* عرض الصور المحذوفة */}
            {imagesToDelete.length > 0 && (
              <div style={{
                marginTop: '15px',
                padding: '15px',
                backgroundColor: '#2a1f1f',
                border: '1px solid #dc3545',
                borderRadius: '8px'
              }}>
                <h6 style={{ color: '#dc3545', marginBottom: '10px', textAlign }}>{t('employees.form.deletedImages.title')}:</h6>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: '10px'
                }}>
                  {imagesToDelete.map((imageId, index) => (
                    <div key={imageId} style={{
                      padding: '8px',
                      backgroundColor: '#3a2a2a',
                      borderRadius: '6px',
                      textAlign: 'center',
                      color: '#dc3545',
                      fontSize: '12px',
                      position: 'relative'
                    }}>
                      {t('employees.form.deletedImages.itemLabel', { index: index + 1 })}
                      <button
                        type="button"
                        onClick={() => {
                          setImagesToDelete(prev => prev.filter(id => id !== imageId));
                          // إعادة الصورة للقائمة الأصلية إذا كانت موجودة
                          const originalImage = employeeDetails?.images?.find(img => img.id === imageId);
                          if (originalImage) {
                            setImages(prev => [...prev, originalImage]);
                          }
                        }}
                        style={{
                          position: 'absolute',
                          top: '-5px',
                          [isRTL ? 'right' : 'left']: '-5px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px'
                        }}
                        title={t('employees.form.deletedImages.restore')}
                      >
                        ↶
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* أزرار الإجراء */}
          <div style={{
            display: 'flex',
            gap: '15px',
            marginTop: '30px',
            justifyContent: 'center'
          }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? '#666' : '#AC2000',
                color: 'white',
                border: 'none',
                padding: '12px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isLoading ? 0.6 : 1,
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid white',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  {t('employees.form.buttons.submittingEdit')}
                </>
              ) : (
                <>
                  <FaSave />
                  {t('employees.form.buttons.submitEdit')}
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '12px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              {t('employees.form.buttons.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
};

const dropdownSearchStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: '#14263b',
  color: 'white',
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: 'center',
  color: '#ccc',
  fontSize: 13,
};

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  isRTL = true,
  i18n,
  itemDisplayKey = 'name',
}) => {
  const { t } = useTranslation();
  const dropdownIsRTL = isRTL !== undefined ? isRTL : (i18n?.language === 'ar');

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div style={{ ...dropdownMenuStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={t('employees.form.placeholders.searchRole', { defaultValue: 'ابحث عن دور...' })}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}
        />
      </div>
      {pagination && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1)) && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>
              {t('employees.form.pagination.page', { defaultValue: 'صفحة' })} {pagination.current_page} {t('employees.form.pagination.of', { defaultValue: 'من' })} {pagination.last_page}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }}
              disabled={pagination.current_page === 1}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ««
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }}
              disabled={pagination.current_page === 1}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ‹
            </button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onPageChange(page); }}
                  style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}
                >
                  {page}
                </button>
              ));
            })()}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }}
              disabled={pagination.current_page === pagination.last_page}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ›
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }}
              disabled={pagination.current_page === pagination.last_page}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              »»
            </button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>
            {t('employees.form.pagination.noResults', { defaultValue: 'لا توجد نتائج' })}
          </div>
        ) : (
          (Array.isArray(items) ? items : []).map((item) => (
            <div
              key={item.id}
              style={{
                padding: '10px 12px',
                borderTop: '1px solid #333',
                cursor: 'pointer',
                direction: dropdownIsRTL ? 'rtl' : 'ltr',
                textAlign: dropdownIsRTL ? 'right' : 'left'
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2a2a2a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {item[itemDisplayKey] || item.name}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EditEmployee;